import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const OAShellFAQ = () => {
  const faqs = [
    {
      question: "Q: How does OA Shell generate office action responses?",
      answer:
        "A: Enter the application number. OA Shell retrieves the Office Action data and auto-generates a structured response draft. You may optionally upload a claims DOCX file.",
    },
    {
      question: "Q: Do I need to upload the Office Action rejection documents?",
      answer:
        "A: Usually no, but in some cases where the system cannot fetch the documents, you will be prompted to upload them manually.",
    },
    {
      question: "Q: Do I need to upload a claims DOCX file?",
      answer:
        "A: OA Shell first attempts to retrieve the latest filed response/amendment DOCX and extract the claims. If a suitable DOCX is not available, you will be prompted to upload the claims DOCX manually. PDF files are not used for claims extraction.",
    },
    {
      question: "Q: How should I name my Rejection PDF and DOCX files for upload?",
      answer: (
        <>
          A: Ensure both files follow the USPTO naming convention and have identical filenames (except for the file extension).
          <br /><br />

          <strong>Format:</strong><br />
          [Customer Number]_[Application Number]_[Mail Room Date]_[Document Code].pdf/.docx
          <br /><br />

          <strong>Example:</strong><br />
          23363_18522217_01-02-2025_CTNF.pdf<br />
          23363_18522217_01-02-2025_CTNF.docx
          <br /><br />

          <strong>Note:</strong> Matching filenames allow OA Shell to correctly pair the documents during processing.
        </>
      ),
    },
    {
      question: "Q: What’s in the response package?",
      answer: (
        <>
          A: The package includes:<br />
          • OA response draft<br />
          • OCR-processed cited references (PDFs)<br />
          • OCR-processed rejection (PDF)<br />
          • <strong>Uploaded/System-retrieved claims source file</strong>
        </>
      ),
    },
    {
      question: "Q: How does OA Shell handle uploaded claims?",
      answer:
        "A: It parses the claims, updates status identifiers, removes formatting (strikethroughs, brackets), and retains clean text.",
    },
    {
      question: "Q: Why is unrelated content appearing in my claims section?",
      answer:
        "A: This may happen if a previously filed response was uploaded instead of a clean claim set. To avoid this, upload a file containing only the claims section, with no extra content.",
    },
    {
      question: "Q: Can I process multiple Office Actions at once?",
      answer: "A: No, OA Shell processes one Office Action at a time.",
    },
    {
      question: "Q: Does OA Shell handle all types of rejections and objections?",
      answer:
        "A: OA Shell addresses the full range of claim rejections (e.g., §101, §102, §103, §112, double patenting), though highly complex or rare patterns need manual adjustments.",
    },
    {
      question: "Q: What if my application is unpublished?",
      answer:
        "A: OA Shell can generate a response draft for unpublished applications. However, you must upload both the rejection PDF and the corresponding Word document for response generation.",
    },
    {
      question: "Q: Why are the claims not appearing in the generated response draft?",
      answer:
        "A: The system extracts claims based on standard claims headings. If the document does not include headings such as “Claims,” “Listing of Claims,” etc. the claims section may not be detected. Add one of these headings before the claims text and try again.",
    },
    {
      question: "Q: What types of Office Action rejections are supported by OA Shell Draft?",
      answer:
        "A: OA Shell Draft currently supports Office Action rejection documents with USPTO document codes CTNF (Non-Final Rejection) and CTFR (Final Rejection).",
    },
  ];

  return (
    <Box>
      {faqs.map((faq, i) => (
        <Accordion
          key={i}
          defaultExpanded={i === 0}
          sx={{
            backgroundColor: "#fff",
            "&.Mui-expanded": { margin: 0 },
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight="bold">
              {faq.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ whiteSpace: "pre-line" }}>
              {faq.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default OAShellFAQ;